import React, { useState } from 'react';
import { X, Save, Loader2, Trash2 } from 'lucide-react';
import DistrictSelect from '../DistrictSelect';
import FutureDatePicker from './FutureDatePicker';

function EditDeliveryModal({ delivery, onClose, onSave, onDelete }) {
  const [formData, setFormData] = useState({
    recipient_name: delivery.recipient_name || '',
    recipient_phone: delivery.recipient_phone || '',
    district: delivery.district || '',
    charge: delivery.charge || '',
    address: delivery.address || '',
    notes: delivery.notes || '',
    delivery_date: delivery.delivery_date ? delivery.delivery_date.split('T')[0] : '',
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState('');

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  }

  function handleDistrictChange(district) {
    setFormData(prev => ({
      ...prev,
      district,
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    
    if (!formData.recipient_name || !formData.recipient_phone) {
      setError('Nombre y teléfono son obligatorios');
      return;
    }

    setSaving(true);
    setError('');

    try {
      await onSave(delivery.id, formData);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al actualizar la entrega');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setDeleting(true);
    setError('');

    try {
      await onDelete(delivery.id);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al eliminar la entrega');
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col animate-in fade-in zoom-in duration-200">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-slate-200">
            <div>
              <h2 className="text-xl font-extrabold text-brand-primary font-montserrat">
                Editar Entrega
              </h2>
              <p className="text-sm text-slate-600 font-montserrat mt-1">
                ID: {delivery.id}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              disabled={saving || deleting}
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
            <div className="space-y-4">
              {/* Recipient Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
                  Nombre del Destinatario *
                </label>
                <input
                  type="text"
                  name="recipient_name"
                  value={formData.recipient_name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary text-sm font-montserrat"
                />
              </div>

              {/* Delivery Date */}
              <FutureDatePicker
                selectedDate={formData.delivery_date}
                onDateChange={(date) => setFormData(prev => ({ ...prev, delivery_date: date }))}
              />

              {/* Recipient Phone */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
                  Teléfono *
                </label>
                <input
                  type="tel"
                  name="recipient_phone"
                  value={formData.recipient_phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary text-sm font-montserrat"
                />
              </div>

              {/* Address */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
                  Dirección
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary text-sm font-montserrat"
                />
              </div>

              {/* District */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
                  Distrito
                </label>
                <DistrictSelect
                  district={formData.district}
                  setDistrict={handleDistrictChange}
                />
              </div>

              {/* Charge */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
                  Monto a Cobrar
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-montserrat text-sm">
                    S/
                  </span>
                  <input
                    type="number"
                    name="charge"
                    value={formData.charge}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary text-sm font-montserrat"
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
                  Notas
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary text-sm font-montserrat resize-none"
                />
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700 font-montserrat">{error}</p>
              </div>
            )}
          </form>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-slate-200">
            {/* Delete Button - Left Side */}
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={saving || deleting}
              className="px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl transition-colors duration-200 font-montserrat flex items-center gap-2 disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" />
              <span>Eliminar</span>
            </button>

            {/* Action Buttons - Right Side */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={saving || deleting}
                className="px-6 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold rounded-xl transition-colors duration-200 font-montserrat disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving || deleting}
                className="px-6 py-2.5 bg-brand-primary hover:bg-brand-primary/90 text-white font-semibold rounded-xl transition-colors duration-200 font-montserrat flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Guardando...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Guardar Cambios</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
            {/* Warning Icon */}
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>

            {/* Title */}
            <h3 className="text-xl font-extrabold text-slate-900 text-center mb-2 font-montserrat">
              ¿Eliminar entrega?
            </h3>

            {/* Message */}
            <p className="text-sm text-slate-600 text-center mb-6 font-montserrat">
              Esta acción eliminará permanentemente la entrega y no podrá ser restaurada. ¿Estás seguro de que deseas continuar?
            </p>

            {/* Delivery Info */}
            <div className="bg-slate-50 rounded-lg p-3 mb-6">
              <p className="text-sm font-medium text-slate-700 font-montserrat">
                <span className="font-bold">Destinatario:</span> {delivery.recipient_name}
              </p>
              <p className="text-sm text-slate-600 font-montserrat mt-1">
                <span className="font-bold">ID:</span> {delivery.id}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="flex-1 px-6 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold rounded-xl transition-colors duration-200 font-montserrat disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl transition-colors duration-200 font-montserrat flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Eliminando...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    <span>Eliminar</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default EditDeliveryModal;